import { Component, ElementRef, Input, input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'kfm-signature-field',
  imports: [CommonModule],
  templateUrl: './kfm-signature-field.component.html',
})
export class KfmSignatureFieldComponent {
  private _canvas: HTMLCanvasElement | null = null;
  private _ctx: CanvasRenderingContext2D | null = null;
  private _isDrawing = false;
  private _imgDataUrl: string | null = null;

  @Input()
  public set imgDataUrl(value: string | null) {
    if (value) {
      this.loadImage(value);
    }

    this._imgDataUrl = value;
  }

  public readonly lineWidth = input<number>(2);
  public readonly strokeStyle = input<string>('#000000');
  public readonly lineCap = input<CanvasLineCap>('round');
  public readonly width = input<number>(400);
  public readonly height = input<number>(200);

  public get signatureDataUrl(): string | null {
    return this._canvas ? this._canvas.toDataURL('image/png') : null;
  }

  @ViewChild('signatureCanvas', { static: true, read: ElementRef })
  public set signatureCanvas(value: ElementRef) {
    if (!value) {
      return;
    }

    this._canvas = value.nativeElement as HTMLCanvasElement;
    this._ctx = this._canvas.getContext('2d');

    if (this._ctx) {
      this._ctx.lineWidth = this.lineWidth();
      this._ctx.strokeStyle = this.strokeStyle();
      this._ctx.lineCap = this.lineCap();
      this._ctx.fillStyle = '#ffffff';
      this._ctx.fillRect(0, 0, this._canvas.width, this._canvas.height);

      // Load existing signature if available
      const imgDataUrl = this._imgDataUrl;
      if (imgDataUrl) {
        this.loadImage(imgDataUrl);
      }
    }
  }

  protected getPosition(event: TouchEvent | MouseEvent) {
    const rect = this._canvas?.getBoundingClientRect();
    const touch = event instanceof TouchEvent ? event.touches[0] : event;

    return {
      x: touch.clientX - (rect?.left ?? 0),
      y: touch.clientY - (rect?.top ?? 0),
    };
  }

  protected startDrawing(event: TouchEvent | MouseEvent) {
    event.preventDefault();
    this._isDrawing = true;
    const pos = this.getPosition(event);
    this._ctx?.beginPath();
    this._ctx?.moveTo(pos.x, pos.y);
  }

  protected draw(event: TouchEvent | MouseEvent) {
    if (!this._isDrawing) return;
    event.preventDefault();
    const pos = this.getPosition(event);
    this._ctx?.lineTo(pos.x, pos.y);
    this._ctx?.stroke();
  }

  protected stopDrawing(event: TouchEvent | MouseEvent) {
    event.preventDefault();
    this._isDrawing = false;
    this._ctx?.closePath();
  }

  private loadImage(dataUrl: string) {
    if (!this._ctx || !this._canvas) {
      return;
    }

    const ctx = this._ctx;
    const canvas = this._canvas;

    const image = new Image();
    image.onload = () => {
      // Ensure the canvas keeps an opaque white background. Using clearRect
      // here would make the canvas transparent again, which can result in a
      // signature PNG without a white background further down the line
      // (e.g. transparent pixels turning black when later converted to JPEG).
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0);
    };
    image.src = dataUrl;
  }
}
